import {
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  Optional,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SessionUser, Permissions, Visibility, Session, ISession } from 'src/app/models/session';
import { User, UserData } from 'src/app/models/user';
import { FSInviteUpdateType, InviteSessionUser } from 'src/app/models/session-share';
import {
  SessionSharedDataService,
  ShareSessionSource,
} from 'src/app/services/session-shared-data.service';
import { SpaceSharingService } from 'src/app/services/space-sharing.service';
import { UserService } from 'src/app/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import { AclService } from 'src/app/services/acl.service';
import { ReferralService } from 'src/app/services/referral.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { SpacesService } from 'src/app/services/spaces.service';
import { environment } from 'src/environments/environment';
import { MatMenuTrigger } from '@angular/material/menu';
import { PanelView } from 'src/app/sessions/panel/panel.component';
import { Section } from 'src/app/sessions/panel/participants-manager/participants-manager.component';
import { Router } from '@angular/router';
import {
  EmailSearchComponent,
  EmailSelectionEvent,
  EmailValidatorType,
} from 'src/app/ui/email-search/email-search.component';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { TOAST_CLASSES } from 'src/app/common/utils/notification-constants';
import { DEFAULT_LANG_CODE } from 'src/app/models/localization.model';
import { ChildModeService } from 'src/app/services/child-mode.service';
import { getSessionUserType } from 'src/app/utilities/space.utils';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { UiService } from '../../../services/ui.service';
import {
  ReferralDialogComponent,
  ReferralSource,
} from '../../../common/referral-dialog/referral-dialog.component';
import { RequestAccessService } from '../request-access/request-access.service';
import { WaitingRoomService } from '../request-access/waiting-room/waiting-room.service';
import { UserSearchSelectionService } from '../../../services/user-search-selection.service';

// The agent's per-space email address, per environment:
//   - Gmail mailbox (default): plus-addressed, agent+<tag>@spacebar.ai
//   - SendGrid Inbound Parse: the tag is the whole local part, <tag>@agent.spacebar.ai
//     (agentEmailPlusAddressing: false, agentEmailDomain: the parse subdomain)
const AGENT_EMAIL_ENV = environment as unknown as {
  agentEmailDomain?: string;
  agentEmailPlusAddressing?: boolean;
};
const AGENT_EMAIL_DOMAIN = AGENT_EMAIL_ENV.agentEmailDomain || 'spacebar.ai';
const agentEmailFor = (tag: string): string =>
  AGENT_EMAIL_ENV.agentEmailPlusAddressing === false
    ? `${tag}@${AGENT_EMAIL_DOMAIN}`
    : `agent+${tag}@${AGENT_EMAIL_DOMAIN}`;

@UntilDestroy()
@Component({
  selector: 'app-share-session-dialog',
  templateUrl: './wb-share-session.component.html',
  styleUrls: ['./wb-share-session.component.scss'],
  providers: [UserSearchSelectionService],
})
export class ShareSessionDialogComponent implements OnDestroy, AfterViewInit {
  usersChangingRoleIDs = new Set<string>();
  private _hostsIDs$ = new BehaviorSubject<Set<string>>(new Set<string>());
  hostIDs$ = this._hostsIDs$.asObservable();
  currentUser?: User;
  showUserList = false;
  allUsersLookup: { [id: string]: User } = {};
  spaceUsersLookup: { [id: string]: SessionUser } = {};
  usersToAdd: InviteSessionUser[] = []; // list of users to add by ID
  usersToAddEmails: string[] = [];
  usersWillBeInvitedEmails: string[] = [];
  usersBeingRemoved: string[] = [];
  addedUsers: SessionUser[] = [];
  shareLink = '';
  agentEmail = '';
  // Whether this space's institution has the agentic AI feature enabled. Gates the
  // agent-email row (mirrors the Start AI button). Server-derived flag.
  aiAgentEnabled = false;
  isCurrentUserHost = false;
  addOwnerUsers = false;
  spaceOwnerId = '';
  canInviteToPencil = false;
  // Whether the ability to invite individual users shows up or not.
  inviteParticipants = false;
  mobileView = false;
  @ViewChild('userSearch') userSearch!: ElementRef;
  freeTimeReferral = false;
  isSearchHovered = false;
  isSubmitClicked = false;
  inviteText = '';
  visibility: Visibility = Visibility.PRIVATE;
  sendNotifyEmail = true;
  spaceId!: string;
  public visibilityEnum = Visibility;
  inviteTextMaxLength = 5000;
  invitedSectionExpanded = false;
  anonymousUserSectionExpanded = false;
  anonymousUsers: SessionUser[] = [];
  pendingInviteUsers: SessionUser[] = [];
  joinedUsers: SessionUser[] = [];
  cantReinviteTooltipUserID = '';
  canReinviteOneOrMore = false;
  addAdminsAsHostsInSpace = false;
  currentSpace?: ISession;
  hoveredUserID = '';
  // Presence of a user ID means role editing is disabled. Empty string means no tooltip.
  editDisabledTooltipTextByUserID: Record<string, string | undefined> = {};
  participantsManagerEnabled: boolean = false;
  private shownJoinedUsers = new BehaviorSubject<SessionUser[]>([]);
  shownJoinedUsers$ = this.shownJoinedUsers.asObservable();

  totalAccessRequestsSize$ = combineLatest([
    this.requestAccessService.accessRequestersSize$,
    this.waitingRoomService.accessRequestersSize$,
  ]).pipe(
    untilDestroyed(this),
    map(([accessRequestersSize, waitingRoomSize]) => accessRequestersSize + waitingRoomSize),
  );
  private readonly INVITE_USERS_BATCH_SIZE = 10;

  isCurrentUserLanguageEnglish$ = this.userService.user.pipe(
    untilDestroyed(this),
    map(
      (user: UserData | null) => this.userService.getUserLanguage(user?.user) === DEFAULT_LANG_CODE,
    ),
  );

  additionalEmailValidators: EmailValidatorType[] = [];

  @ViewChild('matMenuTrigger') trigger!: MatMenuTrigger;
  @ViewChild(EmailSearchComponent) emailSearchComponent?: EmailSearchComponent;

  constructor(
    public userService: UserService,
    public spaceSharingService: SpaceSharingService,
    private spacesService: SpacesService,
    private sharedDataService: SessionSharedDataService,
    private translateService: TranslateService,
    private flagsService: FlagsService,
    private uiService: UiService,
    private router: Router,
    private modalManagerService: ModalManagerService,
    public aclService: AclService,
    private referralService: ReferralService,
    private zone: NgZone,
    public spaceRepo: SpaceRepository,
    private telemetry: TelemetryService,
    public requestAccessService: RequestAccessService,
    public waitingRoomService: WaitingRoomService,
    private notificationToasterService: NotificationToasterService,
    private childModeService: ChildModeService,
    private userSearchSelectionService: UserSearchSelectionService,
    @Optional() private dialogRef: MatDialogRef<ShareSessionDialogComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data?: { spaceId?: string; source?: ShareSessionSource },
  ) {
    const spaceId = this.data?.spaceId ?? this.spaceRepo.activeSpace?._id;
    if (!spaceId) {
      this.close();
      return;
    }
    this.spaceId = spaceId;
    // Seed the AI-enabled flag from the active space (authoritative value is set
    // from the loaded space in the getSpace$ subscription below).
    this.aiAgentEnabled = !!this.spaceRepo.activeSpace?.institution?.aiAgentEnabled;
    // The agent's per-space email address (plus-addressing → one inbox, the tag
    // routes to this space). Emailing it hands the agent a task in this space —
    // attach files, forward docs. Only shown when the institution has the AI agent
    // enabled (see aiAgentEnabled).
    //
    // The tag is a SHORT CODE fetched from the server, not the spaceId: the id
    // form ran to 42 characters and published the space's creation time. Shown
    // optimistically in the long form so the field is never blank, then replaced
    // when the code arrives — and the long form still routes, so a user who copies
    // it in that instant is not handed a broken address.
    this.agentEmail = agentEmailFor(this.spaceId);
    this.spacesService
      .getAgentEmailTag(this.spaceId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({ tag }) => {
          if (tag) {
            this.agentEmail = agentEmailFor(tag);
          }
        },
        error: () => {
          /* keep the id-based address — it resolves too */
        },
      });
    this.shareLink = this.referralService.buildInviteLinkFromSpaceId(
      this.spaceId,
      new URLSearchParams({
        utm_source: 'copy_link',
        utm_medium: 'invite',
      }),
    );
    this.uiService.isMobile.pipe(untilDestroyed(this)).subscribe((res) => {
      this.mobileView = res;
    });
    if (this.flagsService.isFlagEnabled(FLAGS.WB_INVITE_TO_PENCIL)) {
      this.canInviteToPencil = true;
    }
    if (this.flagsService.isFlagEnabled(FLAGS.WB_INVITE_PARTICIPANTS)) {
      this.inviteParticipants = true;
    }
    this.participantsManagerEnabled = this.flagsService.isFlagEnabled(
      FLAGS.SPACES_PARTICIPANTS_MANAGER,
    );
  }

  ngAfterViewInit(): void {
    combineLatest([this.spaceRepo.getSpace$(this.spaceId), this.userService.user])
      .pipe(untilDestroyed(this))
      .subscribe(([space, userData]) => {
        if (space) {
          this.currentSpace = space;
          this.spaceUsersLookup = this.spaceSharingService.buildLookupTable(space.users);
          this.visibility = space.visibility;
          this.inviteText = `${this.translateService.instant(
            "Hey! I'd like to invite you to this Space.",
          )}`;
          space.populatedUsers?.forEach((user) => {
            user.isAnonymous = this.aclService.isAnonymous(user);
            this.allUsersLookup[user._id] = user;
          });
          this.currentUser = userData?.user;
          this.isCurrentUserHost = this.spaceRepo.hasSpaceHostPermissions(this.currentUser, space);
          this.spaceOwnerId = space.owner;
          this.addedUsers = space.users.filter((user) => !!this.allUsersLookup[user._id]);
          this.addedUsers.forEach((sessionUser) => {
            sessionUser.isAnonymous = this.allUsersLookup[sessionUser._id].isAnonymous;
            const isCurrentlyHost = this.spaceRepo.hasSpaceHostPermissions(
              this.allUsersLookup[sessionUser._id],
              space,
            );
            if (isCurrentlyHost === this._hostsIDs$.value.has(sessionUser._id)) {
              this.usersChangingRoleIDs.delete(sessionUser._id);
            } else if (!this.usersChangingRoleIDs.has(sessionUser._id)) {
              this.updateUserInHostsIDs(sessionUser._id, isCurrentlyHost);
            }
            // else if there is any pending changes, keep them after sending invites to users ... don't update hosts set
          });

          this.sortAddedUsers();
          this.updateEditDisabledStateByUserID();

          this.joinedUsers = [];
          this.pendingInviteUsers = [];
          this.anonymousUsers = [];
          this.canReinviteOneOrMore = false;
          this.addedUsers.forEach((user) => {
            if (user.isAnonymous) {
              this.anonymousUsers.push(user);
            } else if (user.joined) {
              this.joinedUsers.push(user);
            } else {
              this.pendingInviteUsers.push(user);
              this.canReinvite(user);
            }
          });

          this.loadMoreUsers();

          this.pendingInviteUsers.sort(
            (a, b) =>
              new Date(b?.lastInviteTime ?? 0).getTime() -
              new Date(a?.lastInviteTime ?? 0).getTime(),
          );

          if (
            this.flagsService.isFlagEnabled(FLAGS.SPACES_FREE_TIME_REFERRAL) &&
            this.currentUser &&
            this.aclService.isGuest(this.currentUser)
          ) {
            this.freeTimeReferral = true;
          }

          this.addAdminsAsHostsInSpace = !!space.institution?.settings?.addAdminsAsHostsInSpace;

          // Authoritative AI-enabled flag from the loaded space's institution.
          this.aiAgentEnabled = !!space.institution?.aiAgentEnabled;

          // Load institution details if needed to check email validation settings
          this.loadInstitutionDetailsIfNeeded();

          // Initialize excluded user IDs observable with permanent exclusions
          this.initPermanentExcludedUserIds();
        }
      });

    this.router.events.pipe(untilDestroyed(this)).subscribe(() => this.close());

    // When child mode is active for current user, close this dialog (invite is not shown in child mode).
    this.childModeService.isChildModeActive$
      .pipe(
        untilDestroyed(this),
        filter((active) => active),
      )
      .subscribe(() => this.close());

    const dialog = document.getElementsByClassName('share-wb-dialog')[0];
    this.zone.runOutsideAngular(() => {
      dialog.addEventListener('keydown', this.preventEventPropogation);
      dialog.addEventListener('keyup', this.preventEventPropogation);
    });
  }

  ngOnDestroy(): void {
    const dialog = document.getElementsByClassName('share-wb-dialog')[0];
    dialog.removeEventListener('keydown', this.preventEventPropogation);
    dialog.removeEventListener('keyup', this.preventEventPropogation);
    if (this.data?.source === ShareSessionSource.SPACES_LIST) {
      this.sharedDataService.cleanuprealtimeSession(this.spaceId);
    }
  }

  loadMoreUsers(): void {
    const currentLength = this.shownJoinedUsers.value.length;
    const newUsers = this.joinedUsers.slice(
      currentLength,
      currentLength + this.INVITE_USERS_BATCH_SIZE,
    );
    this.shownJoinedUsers.next([...this.shownJoinedUsers.value, ...newUsers]);
  }

  preventEventPropogation(e: Event): void {
    e.stopPropagation();
  }

  copyLink(): void {
    navigator.clipboard.writeText(this.shareLink);
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('Link copied'),
      this.notificationToasterService.generateNotificationCode(),
      false,
      TOAST_CLASSES.HIGH_Z_INDEX,
    );
  }

  copyAgentEmail(): void {
    if (!this.agentEmail) {
      return;
    }
    navigator.clipboard.writeText(this.agentEmail);
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('Email copied'),
      this.notificationToasterService.generateNotificationCode(),
      false,
      TOAST_CLASSES.HIGH_Z_INDEX,
    );
  }

  onBlurSearch(): void {
    this.showUserList = false;
  }

  canRemoveParticipant(participant: User): boolean {
    return this.currentUser?._id !== participant?._id && this.isCurrentUserHost;
  }

  /**
   * user removed from the invitation or from the list of the users in the space
   * @param userToRemove
   * @param isUserInSpace if the user is already in the space or not
   */
  onRemoveUser(userToRemove: InviteSessionUser, isUserInSpace: boolean): void {
    this.usersWillBeInvitedEmails = this.usersWillBeInvitedEmails.filter(
      (email) => email !== userToRemove.email,
    );

    if (!userToRemove.isValid()) {
      this.usersToAdd = this.usersToAdd.filter(
        (userToAdd) => userToAdd.isValid() || userToRemove.email !== userToAdd.email,
      );
      return;
    }
    this.usersToAdd = this.usersToAdd.filter(
      (userToAdd) =>
        !userToAdd.isValid() || userToAdd?.sessionUser?._id !== userToRemove?.sessionUser?._id,
    );
    if (userToRemove?.sessionUser) {
      //  user in the space only adding to the array and roleChanged is true only for them
      if (isUserInSpace) {
        this.usersChangingRoleIDs.add(userToRemove.sessionUser._id);
        this.usersBeingRemoved.push(userToRemove.sessionUser._id);
      }
      const userToBeRemoved = this.allUsersLookup[userToRemove?.sessionUser?._id];
      this.usersToAddEmails = this.usersToAddEmails.filter(
        (email) => email !== userToBeRemoved.email,
      );

      // Remove user from excluded list so they can appear in search results again
      this.userSearchSelectionService.removeSelectedUsers([userToRemove.sessionUser]);

      // remove user if it is in the owner list
      if (this._hostsIDs$.value.has(userToRemove?.sessionUser?._id)) {
        const newHostsIDs = new Set(this._hostsIDs$.value);
        newHostsIDs.delete(userToRemove?.sessionUser?._id);
        this._hostsIDs$.next(newHostsIDs);
      }
    }
  }

  // Need to de-duplicate added and removed users...
  submit(): void {
    this.isSubmitClicked = true;
    if (
      !this.spaceRepo.getSpace(this.spaceId) ||
      this.usersToAdd.some((userToAdd) => !userToAdd.isValid())
    ) {
      return;
    }
    const validInviteUsers: SessionUser[] = [];
    const invitedUsers: SessionUser[] = [];
    const reinvitedUsers: SessionUser[] = [];

    this.usersToAdd.forEach((user) => {
      const sessionUser = user.sessionUser;
      if (sessionUser) {
        sessionUser.isOwner = this.addOwnerUsers;
        validInviteUsers.push(sessionUser);
        if (this.spaceUsersLookup[sessionUser._id]) {
          reinvitedUsers.push(sessionUser);
        } else {
          invitedUsers.push(sessionUser);
        }
      }
    });

    const [signedUpInvitedCount, newInvitedCount] = this.spaceSharingService.splitOnSignUpStatus(
      invitedUsers,
      this.allUsersLookup,
    );
    const [signedUpReinvitedCount, newReinvitedCount] =
      this.spaceSharingService.splitOnSignUpStatus(reinvitedUsers, this.allUsersLookup);

    this.spacesService
      .updateSessionUsers(
        this.spaceId,
        validInviteUsers,
        [],
        this.inviteText,
        this.sendNotifyEmail,
        false,
      )
      .pipe(first())
      .subscribe((res) => {
        if (res.ok) {
          this.spaceSharingService.fireFSInviteCountEvent(
            signedUpInvitedCount,
            newInvitedCount,
            FSInviteUpdateType.SPACE_USERS_INVITED,
          );
          this.spaceSharingService.fireFSInviteCountEvent(
            signedUpReinvitedCount,
            newReinvitedCount,
            FSInviteUpdateType.SPACE_USERS_REINVITED,
          );
          this.isSubmitClicked = false;
          this.notificationToasterService.showDefaultSuccessNotification(
            validInviteUsers.length === 1
              ? this.translateService.instant('Invitation sent')
              : this.translateService.instant('Invitations sent'),
            this.notificationToasterService.generateNotificationCode(),
            false,
            TOAST_CLASSES.HIGH_Z_INDEX,
          );

          this.usersToAdd = [];
          this.usersToAddEmails = [];
          this.invitedSectionExpanded =
            this.invitedSectionExpanded || !!validInviteUsers.find((u) => !u.joined);
        }
      });
  }

  updateVisibilityValue(visibility: Visibility) {
    this.visibility = visibility;
  }

  changeVisibility(visibility: Visibility): void {
    if (!this.spaceRepo.getSpace(this.spaceId)) {
      return;
    }
    this.spacesService
      .updateVisibility(this.spaceId, visibility)
      .pipe(first())
      .subscribe((res) => {
        if (res.ok && this.spaceRepo.getSpace(this.spaceId)) {
          this.spaceRepo.updateSpace(this.spaceId, { visibility: visibility });
          this.visibility = visibility;

          this.notificationToasterService.showDefaultSuccessNotification(
            this.translateService.instant('Space visibility updated'),
            this.notificationToasterService.generateNotificationCode(),
            false,
            TOAST_CLASSES.HIGH_Z_INDEX,
          );

          if (this.visibility === Visibility.PUBLIC) {
            this.copyLink();
          }
        }
      });
  }

  changeInvitedUsersRoles(isHost: boolean): void {
    this.addOwnerUsers = isHost;
  }

  saveUserRole(): void {
    if (this.spaceRepo.getSpace(this.spaceId)) {
      const usersToRemove: SessionUser[] = [];
      this.usersBeingRemoved.forEach((id) => {
        usersToRemove.push(
          new SessionUser({
            _id: id,
            permissions: [],
            userPermissions: new Permissions(),
          }),
        );
      });
      this.spacesService
        .updateSessionUsers(
          this.spaceId,
          this.addedUsers.map((user) => ({
            ...user,
            isOwner: this._hostsIDs$.value.has(user._id),
          })),
          usersToRemove,
        )
        .pipe(first())
        .subscribe(() => {
          this.addedUsers = this.addedUsers.filter(
            (addedUser) => !this.usersBeingRemoved.includes(addedUser._id),
          );
          this.shownJoinedUsers.next(
            this.shownJoinedUsers.value.filter(
              (addedUser) => !this.usersBeingRemoved.includes(addedUser._id),
            ),
          );
          this.usersChangingRoleIDs.clear();

          this.notificationToasterService.showDefaultSuccessNotification(
            this.usersBeingRemoved.length
              ? this.translateService.instant('Person removed')
              : this.translateService.instant('Role changed'),
            this.notificationToasterService.generateNotificationCode(),
            false,
            TOAST_CLASSES.HIGH_Z_INDEX,
          );

          this.usersBeingRemoved = [];
        });
    }
  }

  changeUserRole(sessionUser: SessionUser, isNewRoleHost: boolean): void {
    const isCurrentlyHost = Session.isOwnedByUser(
      this.spaceRepo.getSpace(this.spaceId),
      sessionUser._id,
    );
    if (isCurrentlyHost === isNewRoleHost) {
      this.usersChangingRoleIDs.delete(sessionUser._id);
    } else {
      this.usersChangingRoleIDs.add(sessionUser._id);
    }
    this.updateUserInHostsIDs(sessionUser._id, isNewRoleHost);
  }

  close(): void {
    this.dialogRef?.close(this.spaceRepo.getSpace(this.spaceId));
  }

  inviteToPencil(email: string): void {
    // preventing making the request to the same user twice
    if (this.usersWillBeInvitedEmails.includes(email)) {
      return;
    }

    this.usersWillBeInvitedEmails.push(email);
    this.userService
      .inviteUser(email)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        if (user) {
          this.allUsersLookup[user._id] = user;
          this.handleUserSelection(user);
        }
      });
  }

  mobileShare(): void {
    const shareData = {
      title: this.translateService.instant('Pencil spaces'),
      text: this.translateService.instant('Share current Pencil space'),
      url: location.href,
    };
    navigator.share(shareData);
  }

  shareSessionReferral(): void {
    const shareSessionReferralParams = {
      panelClass: 'referral-wb-dialog',
      autoFocus: false,
      data: {
        referralSource: ReferralSource.INVITE,
        sessionId: this.spaceId,
      },
    };

    this.modalManagerService.showModal(ReferralDialogComponent, shareSessionReferralParams);
    this.close();
  }

  SessionUserAlphabeticalComparator(a: SessionUser, b: SessionUser): number {
    const a_key = this.allUsersLookup[a._id].name ?? this.allUsersLookup[a._id].email;
    const b_key = this.allUsersLookup[b._id].name ?? this.allUsersLookup[b._id].email;
    return a_key.localeCompare(b_key);
  }

  sortAddedUsers() {
    const owner: SessionUser[] = [];
    const currentHost: SessionUser[] = [];
    const hosts: SessionUser[] = [];
    const participants: SessionUser[] = [];
    this.addedUsers.forEach((user) => {
      if (this._hostsIDs$.value.has(user._id)) {
        if (this.spaceOwnerId === user._id) {
          owner.push(user);
        } else if (this.currentUser?._id === user._id) {
          currentHost.push(user);
        } else {
          hosts.push(user);
        }
      } else {
        participants.push(user);
      }
    });

    participants.sort((a, b) => this.SessionUserAlphabeticalComparator(a, b));
    hosts.sort((a, b) => this.SessionUserAlphabeticalComparator(a, b));

    this.addedUsers = [...owner, ...currentHost, ...hosts, ...participants];
  }

  resetFilteredUsers(): void {
    // Reset state variables
    this.usersToAdd = [];
    this.usersToAddEmails = [];
    this.usersWillBeInvitedEmails = [];
    this.isSubmitClicked = false;

    // Reinitialize excluded user IDs observable with permanent exclusions only
    this.initPermanentExcludedUserIds();

    this.emailSearchComponent?.resetErrorState();
  }

  /**
   * Initializes excluded user IDs with permanent exclusions from session state.
   * Called during component initialization and when resetting the selection.
   */
  private initPermanentExcludedUserIds(): void {
    const permanentExclusions: (User | SessionUser)[] = [];

    // Exclude current user
    if (this.currentUser) {
      permanentExclusions.push(this.currentUser);
    }

    // Exclude space owner (if we have a user object for it)
    if (this.spaceOwnerId && this.allUsersLookup[this.spaceOwnerId]) {
      permanentExclusions.push(this.allUsersLookup[this.spaceOwnerId]);
    }

    // Exclude pending and joined users
    permanentExclusions.push(...this.pendingInviteUsers, ...this.joinedUsers);

    this.userSearchSelectionService.initializeSelectedUsers(permanentExclusions);
  }

  getUserInviteInfo(email: string, sessionUser?: SessionUser, id?: string): InviteSessionUser {
    if (sessionUser) {
      return new InviteSessionUser(email, sessionUser);
    } else if (id) {
      return new InviteSessionUser(
        email,
        this.spaceUsersLookup[id] ?? {
          _id: id,
          permissions: [],
          userPermissions: new Permissions(),
          isOwner: this.addOwnerUsers,
        },
      );
    }
    return new InviteSessionUser(email);
  }

  updateUserInHostsIDs(id: string, isHost: boolean) {
    if (isHost) {
      this._hostsIDs$.next(new Set([...this._hostsIDs$.value, id]));
    } else {
      this._hostsIDs$.next(new Set([...this._hostsIDs$.value].filter((hostId) => hostId !== id)));
    }
  }

  canReinvite(sessionUser: SessionUser | undefined) {
    if (!sessionUser || sessionUser._id === this.currentUser?._id) {
      return false;
    }
    const isReinvitable =
      Date.now() - new Date(sessionUser.lastInviteTime ?? 0).getTime() >=
      this.spaceSharingService.REINVITE_TIME_IN_MILLIS;
    if (isReinvitable) {
      this.canReinviteOneOrMore = this.canReinviteOneOrMore || isReinvitable;
      if (sessionUser._id === this.cantReinviteTooltipUserID) {
        this.cantReinviteTooltipUserID = '';
      }
    }
    return isReinvitable;
  }

  removeAllAnonymousUsers(): void {
    for (const user of this.anonymousUsers) {
      const sessionUser = this.getUserInviteInfo(this.allUsersLookup[user._id].email, user);
      this.onRemoveUser(sessionUser, true);
    }
  }

  resendInviteToAllEligible() {
    this.resendInvite(
      this.pendingInviteUsers.filter((sessionUser) => this.canReinvite(sessionUser)),
    );
  }

  resendInviteToSingleUser(user: SessionUser) {
    this.resendInvite([user]);
  }

  resendInvite(reinvitedUsers: SessionUser[]) {
    const [signedUpReinvitedCount, newReinvitedCount] =
      this.spaceSharingService.splitOnSignUpStatus(reinvitedUsers, this.allUsersLookup);
    this.spacesService
      .updateSessionUsers(
        this.spaceId,
        reinvitedUsers,
        [],
        this.inviteText,
        this.sendNotifyEmail,
        false,
      )
      .pipe(first())
      .subscribe((res) => {
        if (res.ok) {
          this.notificationToasterService.showDefaultSuccessNotification(
            reinvitedUsers.length === 1
              ? this.translateService.instant('Invitation sent')
              : this.translateService.instant('Invitations sent'),
            this.notificationToasterService.generateNotificationCode(),
            false,
            TOAST_CLASSES.HIGH_Z_INDEX,
          );

          this.spaceSharingService.fireFSInviteCountEvent(
            signedUpReinvitedCount,
            newReinvitedCount,
            FSInviteUpdateType.SPACE_USERS_REINVITED,
          );
          this.invitedSectionExpanded = true;
        }
      });
  }

  minutesUntilReinvite(sessionUser: SessionUser | undefined): number {
    const diffInMillis = !sessionUser
      ? 0
      : Date.now() - new Date(sessionUser.lastInviteTime ?? 0).getTime();
    const remInMillis = Math.max(
      this.spaceSharingService.REINVITE_TIME_IN_MILLIS - diffInMillis,
      0,
    );
    return Math.ceil(remInMillis / (60 * 1000));
  }

  getReinviteTooltipText(sessionUser: SessionUser | undefined): string {
    // TODO: We need to remove this function call from the template
    const minutes = this.minutesUntilReinvite(sessionUser);
    if (minutes < 1) {
      return this.translateService.instant('You can re-invite this user in less than a minute.');
    } else {
      return this.translateService.instant('You can re-invite this user in {{minutes}} minutes', {
        minutes,
      });
    }
  }

  getTimeSince(lastInviteTime: Date | undefined) {
    const oldUnixTime = new Date(lastInviteTime ?? 0).getTime();
    const newUnixTime = Date.now();
    const diff = newUnixTime - oldUnixTime;
    if (oldUnixTime === 0) {
      return '';
    }

    return this.spaceSharingService.getLocalizedTimeSince(diff, Math.floor);
  }

  setReinviteTooltip(inviteSessionUserID: string) {
    if (this.currentUser?._id !== inviteSessionUserID) {
      this.cantReinviteTooltipUserID = inviteSessionUserID;
    }
  }

  private updateEditDisabledStateByUserID(): void {
    this.editDisabledTooltipTextByUserID = {};

    // Without edit permissions, all role controls stay disabled without row-level tooltips.
    if (!this.currentUser || !this.currentSpace || !this.isCurrentUserHost) {
      this.addedUsers.forEach((addedSpaceUser) => {
        this.editDisabledTooltipTextByUserID[addedSpaceUser._id] = '';
      });
      return;
    }

    const currentUser = this.currentUser;
    const currentSpace = this.currentSpace;

    this.addedUsers.forEach((addedSpaceUser) => {
      // Hosts can edit others, but not themselves. No tooltip is needed for the current user.
      if (currentUser._id === addedSpaceUser._id) {
        this.editDisabledTooltipTextByUserID[addedSpaceUser._id] = '';
        return;
      }

      // Space owners and admin-granted hosts cannot have their role changed
      if (
        currentSpace.owner === addedSpaceUser._id ||
        Session.isUserEnabledAdminHost(addedSpaceUser._id, currentSpace, this.aclService)
      ) {
        this.editDisabledTooltipTextByUserID[addedSpaceUser._id] = this.translateService.instant(
          'The role of a Space {{userType}} can not be changed',
          {
            userType: getSessionUserType(
              addedSpaceUser,
              this._hostsIDs$.value,
              currentSpace,
              this.aclService,
              this.translateService,
            ),
          },
        );
        return;
      }

      // Cohort members' roles are managed by the cohort
      if (this.isCohortMember(addedSpaceUser._id)) {
        this.editDisabledTooltipTextByUserID[addedSpaceUser._id] = this.translateService.instant(
          'Roles for Cohort members cannot be changed',
        );
      }
    });
  }

  private isCohortMember(userID: string): boolean {
    const cohort = this.currentSpace?.cohort;
    if (!cohort) {
      return false;
    }

    return (
      cohort.organizers.some((organizerID) => String(organizerID) === userID) ||
      cohort.attendees.some((attendeeID) => String(attendeeID) === userID)
    );
  }

  copyHoveredUserEmail() {
    const userEmail = this.allUsersLookup[this.hoveredUserID]?.email;
    if (!userEmail) {
      return;
    }

    navigator.clipboard.writeText(userEmail).catch((error) => {
      if (!environment.production) {
        console.log(error);
      }
    });

    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('Email copied'),
      this.notificationToasterService.generateNotificationCode(),
      false,
      TOAST_CLASSES.HIGH_Z_INDEX,
    );
  }

  copyEmailsFromList(sessionUserList: SessionUser[]) {
    if (sessionUserList.length === 0) {
      return;
    }
    const copiedEmails = sessionUserList
      .filter((sessionUser) => sessionUser._id !== this.currentUser?._id)
      .map((sessionUser) => this.getEmailInFormat(sessionUser._id))
      .filter((email) => email)
      .join(', ');

    navigator.clipboard.writeText(copiedEmails).catch((error) => {
      if (!environment.production) {
        console.log(error);
      }
    });

    this.notificationToasterService.showDefaultSuccessNotification(
      sessionUserList.length === 1
        ? this.translateService.instant('Email copied')
        : this.translateService.instant('Emails copied'),
      this.notificationToasterService.generateNotificationCode(),
      false,
      TOAST_CLASSES.HIGH_Z_INDEX,
    );
  }

  getEmailInFormat(userID: string): string {
    const user = this.allUsersLookup[userID];
    if (!user) {
      return '';
    }
    return user.name && user.email ? `${user.name} <${user.email}>` : `${user.email}`;
  }

  moveToAccessRequesters() {
    this.close();
    this.sharedDataService.changeParticipantsManagerSection.next(Section.QUEUE);
    if (this.sharedDataService.changeRightPanelView.value !== PanelView.participantsManager) {
      this.sharedDataService.changeRightPanelView.next(PanelView.participantsManager);
    }
  }

  trackById(index: number, user: SessionUser) {
    return user._id;
  }

  handleUserSelection(user: User): void {
    // Check if user was recently invited (within the past 5 minutes)
    const sessionUser = this.spaceUsersLookup[user._id];
    if (sessionUser && !this.canReinvite(sessionUser)) {
      this.telemetry.event('user_invite_suggestion_click', {
        currentUserEmail: this.currentUser?.email,
        currentUserName: this.currentUser?.name,
        userToAddEmail: user.email,
        userToAddName: user.name,
        reason: 'user_recently_invited',
      });
      return;
    }

    const userInviteInfo = this.getUserInviteInfo(user.email, undefined, user._id);
    // Add to usersToAdd
    this.usersToAdd.push(userInviteInfo);

    // Publish telemetry event
    this.telemetry.event('user_invite_suggestion_click', {
      currentUserEmail: this.currentUser?.email,
      currentUserName: this.currentUser?.name,
      userToAddEmail: user.email,
      userToAddName: user.name,
    });

    // Add to usersToAddEmails - always store plain email
    this.usersToAddEmails = [...this.usersToAddEmails, user.email];

    // Exclude the newly selected user from search results
    this.userSearchSelectionService.addSelectedUsers([user]);
    this.allUsersLookup[user._id] = user;
  }

  handleEmailSelection(event: EmailSelectionEvent): void {
    if (event.emails.length === 0) {
      this.resetFilteredUsers();
      return;
    }

    const { newEmails, removedEmails } = this.getNewAndRemovedEmails(event);
    this.handleRemovedEmails(removedEmails);
    this.handleNewEmails(newEmails);

    // Update reference if emails were filtered out compared to what was received
    if (newEmails.length < event.emails.length - removedEmails.length) {
      this.usersToAddEmails = [...this.usersToAddEmails];
    }
  }

  private getNewAndRemovedEmails(event: EmailSelectionEvent): {
    newEmails: string[];
    removedEmails: string[];
  } {
    // Identify which emails were removed and which are new
    const removedEmails = this.usersToAddEmails.filter((email) => !event.emails.includes(email));
    const newEmails = event.emails.filter(
      (email) =>
        !this.usersToAddEmails.includes(email) &&
        !Object.values(this.spaceUsersLookup).some(
          (user) => this.allUsersLookup[user._id]?.email === email,
        ),
    );

    return { newEmails, removedEmails };
  }

  private handleRemovedEmails(removedEmails: string[]): void {
    if (removedEmails.length === 0) {
      return;
    }

    const removedUsers = removedEmails
      .map((email) => this.findUserByEmail(email))
      .filter((user) => user !== undefined) as User[];
    this.userSearchSelectionService.removeSelectedUsers(removedUsers);
    // Remove from invitation list
    this.usersToAdd = this.usersToAdd.filter((user) => !removedEmails.includes(user.email));
    this.usersToAddEmails = this.usersToAddEmails.filter((email) => !removedEmails.includes(email));
    this.usersWillBeInvitedEmails = this.usersWillBeInvitedEmails.filter(
      (email) => !removedEmails.includes(email),
    );
  }

  private handleNewEmails(newEmails: string[]): void {
    newEmails.forEach((email: string) => {
      // Special case for Pencil invites (external emails)
      if (
        this.canInviteToPencil &&
        this.allUsersLookup[email] === undefined &&
        !this.emailSearchComponent?.emailErrorMap.has(email)
      ) {
        this.inviteToPencil(email);
      } else {
        // Try to find user in allUsersLookup or create a minimal User object
        const user = this.findUserByEmail(email) || ({ email } as User);
        this.handleUserSelection(user);
      }
    });
  }

  private findUserByEmail(email: string): User | undefined {
    return Object.values(this.allUsersLookup).find((u) => u.email === email);
  }

  /**
   * Loads institution details if not already populated to check email validation settings.
   * This is needed when opening from space details (e.g., spaces manager) where the
   * institution object isn't populated like when opened from inside the space.
   */
  private async loadInstitutionDetailsIfNeeded(): Promise<void> {
    // Fetch institution if not already populated
    if (!this.currentSpace?.institution && this.currentSpace?.institutionID) {
      try {
        const institution = await firstValueFrom(
          this.userService.getInstitutionDetails(this.currentSpace.institutionID),
        );
        this.currentSpace.institution = institution;
      } catch (error) {
        console.error('Failed to load institution details', error);

        this.notificationToasterService.showDefaultErrorNotificationV2(
          this.translateService.instant('Sorry, there was a problem'),
          this.translateService.instant(
            'An error occurred while loading institution details. Please try again.',
          ),
          this.notificationToasterService.generateNotificationCode(),
          undefined,
          undefined,
          undefined,
          true,
          undefined,
          TOAST_CLASSES.HIGH_Z_INDEX,
        );
      }
    }

    // Enable external user validation if institution setting is enabled
    if (this.currentSpace?.institution?.settings?.preventAddingExternalUsersToSpaces) {
      this.additionalEmailValidators = [EmailValidatorType.EXTERNAL_USER];
    }
  }
}
